(() => {
  const moneyBRL = (cents) => {
    const value = (Number(cents) || 0) / 100;
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const uniq = () => Math.random().toString(16).slice(2) + Date.now().toString(16);

  function initComboBuilder(sectionRoot) {
    const sectionId = sectionRoot.getAttribute('class')?.match(/combo-builder-(\w+)/)?.[1];

    const grid = sectionRoot.querySelector('[data-combo-grid]');
    const facets = sectionRoot.querySelector('[data-combo-facets]');
    // There may be more than one search input (sidebar). We'll use the first.
    const searchInput = sectionRoot.querySelector('[data-combo-search]');
    const sidebar = sectionRoot.querySelector('[data-combo-sidebar]');
    const summary = sectionRoot.querySelector('[data-combo-summary]');
    const toolbar = sectionRoot.querySelector('[data-combo-toolbar]');

    if (!grid || !sidebar || !summary) return;

    function updateToolbarHeightVar() {
      if (!toolbar) return;
      const h = Math.ceil(toolbar.getBoundingClientRect().height || 0);
      sectionRoot.style.setProperty('--combo-toolbar-h', `${h}px`);
    }

    updateToolbarHeightVar();
    // keep in sync on resize / responsive changes
    window.addEventListener('resize', updateToolbarHeightVar);
    if (window.ResizeObserver && toolbar) {
      const ro = new ResizeObserver(() => updateToolbarHeightVar());
      ro.observe(toolbar);
    }

    const defaultBoxId = summary.dataset.defaultBoxId || '';

    function resolveComboId() {
      const selected = sectionRoot.querySelector('[data-combo-box-size]:checked');
      return selected?.dataset?.label?.trim() || uniq();
    }

    // State
    const state = {
      activeCollectionHandle: grid.dataset.activeCollectionHandle,
      maxItems: 4,
      boxId: defaultBoxId,
      items: [], // {variantId, title, price}
      qty: 1,
      comboId: resolveComboId()
    };

    function resolveMaxItems() {
      const selected = sectionRoot.querySelector('[data-combo-box-size]:checked');
      const countAttr = selected?.dataset?.itemsCount;
      const count = Number(countAttr || 0);
      state.maxItems = count || state.maxItems;

      // Update comboId to reflect the selected box label
      state.comboId = resolveComboId();

      // Slot sizing mode (4 slots = large rectangles, >=6 = square)
      if (summary) {
        summary.dataset.slotMode = state.maxItems <= 4 ? '4' : '6';
      }
    }

    function renderSlots() {
      const slots = summary.querySelector('[data-combo-slots]');
      const totalEls = summary.querySelectorAll('[data-combo-total]');
      const errorEl = summary.querySelector('[data-combo-error]');

      if (!slots || !totalEls.length) return;

      // Slots
      slots.innerHTML = '';
      const max = state.maxItems;
      for (let i = 0; i < max; i++) {
        const item = state.items[i];
        const el = document.createElement('div');
        el.className = 'combo-slot';
        if (!item) {
          el.innerHTML = `
            <div class="combo-slot__empty" aria-hidden="true">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="32" height="32" rx="16" fill="#D1D1D1"/>
                <path d="M17.5609 17.3058V21.4018H14.048V17.3058H9.952V13.8213H14.048V9.72533H17.5609V13.8213H21.6284V17.3058H17.5609Z" fill="#B0B0B0"/>
              </svg>
            </div>
          `;
        } else {
          const imgHtml = item.image
            ? `<img class="combo-slot__img" src="${item.image}" alt="${item.title}" loading="lazy">`
            : `<div class="combo-slot__title">${item.title}</div>`;

          el.innerHTML = `
            <div class="combo-slot__filled">
              ${imgHtml}
              <button type="button" class="combo-slot__remove" data-remove-index="${i}" aria-label="Remover">×</button>
            </div>
          `;
        }
        slots.appendChild(el);
      }

      // Total
      const sum = state.items.reduce((acc, it) => acc + (Number(it.price) || 0), 0);
      const totalCents = sum * state.qty;
      totalEls.forEach((el) => { el.textContent = moneyBRL(totalCents); });

      // Errors
      if (errorEl) errorEl.hidden = true;

      // Update cards state
      updateCardButtons();
    }

    function updateCardButtons() {
      const selectedIds = new Set(state.items.map((i) => String(i.variantId)));
      const cards = grid.querySelectorAll('[data-combo-card]');
      cards.forEach((card) => {
        const variantId = String(card.dataset.variantId || '');

        // selected styling
        if (selectedIds.has(variantId)) card.classList.add('is-selected');
        else card.classList.remove('is-selected');

        // If we ever add a button back, keep the same logic for disabling.
        const btn = card.querySelector('[data-combo-add]');
        if (!btn) return;

        if (selectedIds.has(variantId)) {
          btn.disabled = true;
          btn.textContent = 'Selecionado';
          return;
        }

        if (state.items.length >= state.maxItems) {
          btn.disabled = true;
          btn.textContent = 'Caixa cheia';
          return;
        }

        btn.disabled = false;
        btn.textContent = 'Adicionar';
      });
    }

    function showError(msg) {
      const errorEl = summary.querySelector('[data-combo-error]');
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.hidden = false;
      setTimeout(() => { errorEl.hidden = true; }, 2500);
    }

    // Collection fetching
    function buildCollectionViewUrl(handle, baseSearch) {
      // Always produce: /collections/{handle}?view=combo-builder&...
      const url = new URL(`/collections/${handle}`, window.location.origin);

      // Preserve existing query params (utm/filter/sort/page), if provided
      const search = baseSearch || window.location.search || '';
      if (search) {
        const current = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
        current.forEach((v, k) => url.searchParams.append(k, v));
      }

      // Force our view param (overrides any existing one)
      url.searchParams.set('view', 'combo-builder');

      return url.toString();
    }

    async function fetchComboView(handle, baseSearch) {
      const url = buildCollectionViewUrl(handle, baseSearch);
      const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) throw new Error('Falha ao carregar produtos');
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newFacets = doc.querySelector('[data-combo-facets]');
      const newGrid = doc.querySelector('[data-combo-grid]');
      return { newFacets, newGrid, fetchedUrl: url };
    }

    async function loadAllPagesFromGrid() {
      // Looks for a hidden next-url marker produced by combo-grid.liquid
      let nextEl = grid.querySelector('[data-combo-next-url]');
      let nextUrl = nextEl?.getAttribute('data-combo-next-url') || '';

      // Fetch and append until no next page.
      while (nextUrl) {
        try {
          const u = new URL(nextUrl, window.location.origin);
          const { newGrid } = await fetchComboView(state.activeCollectionHandle, u.search);

          const newItems = newGrid?.querySelector('[data-combo-grid-items]');
          const curItems = grid.querySelector('[data-combo-grid-items]');
          if (newItems && curItems) {
            curItems.insertAdjacentHTML('beforeend', newItems.innerHTML);
          }

          // Update nextUrl from the newly fetched grid
          const newNext = newGrid?.querySelector('[data-combo-next-url]');
          nextUrl = newNext?.getAttribute('data-combo-next-url') || '';

          // Re-wire for newly appended cards
          wireGridEvents();
          applyLocalSearch();
          renderSlots();
        } catch (e) {
          // Stop trying if any request fails
          break;
        }
      }

      // Remove any leftover marker in current DOM
      grid.querySelectorAll('[data-combo-next-url]').forEach((el) => el.remove());
    }

    function syncSortSelectFromFacets() {
      const toolbarSelect = sectionRoot.querySelector('[data-combo-sort-select]');
      if (!toolbarSelect || !facets) return;

      const facetSelect = facets.querySelector('select[name="sort_by"]');
      if (!facetSelect) return;

      const current = facetSelect.value;

      // Rebuild options to match theme exactly
      toolbarSelect.innerHTML = '';
      Array.from(facetSelect.options).forEach((opt) => {
        const o = document.createElement('option');
        o.value = opt.value;
        o.textContent = opt.textContent;
        if (opt.value === current) o.selected = true;
        toolbarSelect.appendChild(o);
      });

      // Fallback if no option selected
      if (!toolbarSelect.value && current) toolbarSelect.value = current;
    }

    async function applyFiltersFromSearch(search) {
      const { newFacets, newGrid, fetchedUrl } = await fetchComboView(state.activeCollectionHandle, search || '');
      if (newFacets && facets) facets.innerHTML = newFacets.innerHTML;
      if (newGrid) grid.innerHTML = newGrid.innerHTML;

      syncSortSelectFromFacets();

      // Keep page URL in sync (store filters on the page URL, but never include view=...)
      try {
        const u = new URL(fetchedUrl);
        u.searchParams.delete('view');
        window.history.replaceState({}, '', `${window.location.pathname}${u.search}`);
      } catch (e) {}

      wireGridEvents();
      wireCollectionButtons();
      wireFacets();
      applyLocalSearch();
      renderSlots();

      await loadAllPagesFromGrid();
    }

    async function setActiveCollection(handle) {
      state.activeCollectionHandle = handle;
      grid.dataset.activeCollectionHandle = handle;
      await applyFiltersFromSearch(window.location.search || '');
    }

    function addFromCard(card) {
      if (!card) return;

      const variantId = String(card.dataset.variantId || '');
      if (!variantId) return;

      // Already selected
      if (state.items.some((i) => String(i.variantId) === variantId)) return;

      if (state.items.length >= state.maxItems) {
        showError('Caixa cheia. Remova um item para adicionar outro.');
        return;
      }

      state.items.push({
        variantId,
        title: card.dataset.title,
        price: Number(card.dataset.price) || 0,
        image: card.dataset.image || ''
      });
      renderSlots();
    }

    function wireGridEvents() {
      // Click anywhere on the card adds the item
      grid.querySelectorAll('[data-combo-card]').forEach((card) => {
        if (card.dataset.comboWired === '1') return;
        card.dataset.comboWired = '1';

        card.addEventListener('click', (e) => {
          if (e.target.closest('button')) return;
          addFromCard(card);
        });

        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            addFromCard(card);
          }
        });
      });

      // Explicit Add buttons
      grid.querySelectorAll('[data-combo-add]').forEach((btn) => {
        btn.addEventListener('click', () => {
          addFromCard(btn.closest('[data-combo-card]'));
        });
      });

      // Pagination links (AJAX)
      grid.querySelectorAll('[data-combo-pagination] a').forEach((a) => {
        a.addEventListener('click', async (e) => {
          e.preventDefault();
          const href = a.getAttribute('href');
          if (!href) return;
          const u = new URL(href, window.location.origin);
          const urlSearch = u.search;
          const { newFacets, newGrid, fetchedUrl } = await fetchComboView(state.activeCollectionHandle, urlSearch);
          if (newFacets && facets) facets.innerHTML = newFacets.innerHTML;
          if (newGrid) grid.innerHTML = newGrid.innerHTML;

          // Keep page URL in sync (without setting view=... on the page)
          try {
            const fu = new URL(fetchedUrl);
            fu.searchParams.delete('view');
            window.history.replaceState({}, '', `${window.location.pathname}${fu.search}`);
          } catch (e) {}
          wireGridEvents();
          applyLocalSearch();
          renderSlots();
        });
      });
    }

    function applyLocalSearch() {
      const q = (searchInput?.value || '').trim().toLowerCase();
      const cards = grid.querySelectorAll('[data-combo-card]');
      cards.forEach((card) => {
        const title = (card.dataset.title || '').toLowerCase();
        card.style.display = !q || title.includes(q) ? '' : 'none';
      });
    }

    // Sidebar events
    function wireCollectionButtons() {
      sidebar.querySelectorAll('[data-combo-collection-button]').forEach((btn) => {
        if (btn.dataset.comboWired === '1') return;
        btn.dataset.comboWired = '1';

        btn.addEventListener('click', async () => {
          const handle = btn.dataset.collectionHandle;
          if (!handle) return;

          sidebar.querySelectorAll('[data-combo-collection-button]').forEach((b) => b.classList.remove('is-active'));
          btn.classList.add('is-active');

          try {
            await setActiveCollection(handle);
          } catch (e) {
            showError(e?.message || 'Erro ao carregar');
          }
        });
      });
    }

    function wireFacets() {
      if (!facets) return;
      facets.dataset.comboWired = '0';

      if (facets.dataset.comboWired === '1') return;
      facets.dataset.comboWired = '1';

      // Intercept facet form submits/changes and refresh grid via our endpoint.
      const applyFromForm = async (form) => {
        if (!form) return;
        const fd = new FormData(form);
        const sp = new URLSearchParams();
        for (const [k, v] of fd.entries()) {
          if (v === '' || v == null) continue;
          sp.append(k, v.toString());
        }
        const search = sp.toString() ? `?${sp.toString()}` : '';
        try {
          await applyFiltersFromSearch(search);
        } catch (e) {
          showError(e?.message || 'Erro ao filtrar');
        }
      };

      facets.addEventListener('submit', (e) => {
        const form = e.target.closest('form');
        if (!form) return;
        e.preventDefault();
        applyFromForm(form);
      });

      facets.addEventListener('change', (e) => {
        const form = e.target.closest('form');
        if (!form) return;
        // Most facet UIs rely on change to auto-submit.
        applyFromForm(form);
      });

      facets.addEventListener('click', (e) => {
        // Handle links inside facets (e.g., "Clear", pagination-like links)
        const a = e.target.closest('a');
        if (!a) return;
        const href = a.getAttribute('href');
        if (!href) return;
        try {
          const u = new URL(href, window.location.origin);
          e.preventDefault();
          applyFiltersFromSearch(u.search);
        } catch (_) {}
      });
    }

    wireCollectionButtons();
    wireFacets();

    sectionRoot.querySelectorAll('[data-combo-box-size]').forEach((input) => {
      input.addEventListener('change', () => {
        resolveMaxItems();
        // If items exceed new max, trim
        if (state.items.length > state.maxItems) state.items = state.items.slice(0, state.maxItems);
        renderSlots();
      });
    });

    // Summary events
    summary.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-remove-index]');
      if (!btn) return;
      const idx = Number(btn.dataset.removeIndex);
      if (Number.isNaN(idx)) return;
      state.items.splice(idx, 1);
      renderSlots();
    });

    summary.querySelector('[data-combo-qty-dec]')?.addEventListener('click', () => {
      state.qty = Math.max(1, (state.qty || 1) - 1);
      summary.querySelector('[data-combo-qty]').value = String(state.qty);
      renderSlots();
    });
    summary.querySelector('[data-combo-qty-inc]')?.addEventListener('click', () => {
      state.qty = (state.qty || 1) + 1;
      summary.querySelector('[data-combo-qty]').value = String(state.qty);
      renderSlots();
    });
    summary.querySelector('[data-combo-qty]')?.addEventListener('change', (e) => {
      const v = Math.max(1, Number(e.target.value) || 1);
      state.qty = v;
      e.target.value = String(v);
      renderSlots();
    });

    summary.querySelector('[data-combo-add-to-cart]')?.addEventListener('click', async () => {
      if (!state.items.length) {
        showError('Selecione pelo menos 1 item.');
        return;
      }

      const addBtn = summary.querySelector('[data-combo-add-to-cart]');
      const addBtnOriginalText = addBtn ? addBtn.lastChild?.textContent?.trim() : null;
      if (addBtn) {
        addBtn.disabled = true;
        // Replace text node keeping the icon span intact
        if (addBtn.lastChild?.nodeType === Node.TEXT_NODE) {
          addBtn.lastChild.textContent = ' Adicionando...';
        }
      }

      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');

      // Determine which sections to request so renderContents gets proper HTML
      const sectionsNeeded = [];
      if (cartDrawer) {
        sectionsNeeded.push('cart-drawer', 'cart-icon-bubble');
      } else if (cartNotification) {
        sectionsNeeded.push('cart-notification-product', 'cart-notification-button', 'cart-icon-bubble');
      }

      const items = state.items.map((it) => ({
        id: Number(it.variantId),
        quantity: state.qty,
        properties: {
          box: state.comboId
        }
      }));

      try {
        const payload = { items };
        if (sectionsNeeded.length) payload.sections = sectionsNeeded;

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('Falha ao adicionar ao carrinho');

        let parsedState = null;
        try { parsedState = await res.json(); } catch (e) {}

        if (cartDrawer) {
          // Remove is-empty so CSS shows the drawer content (renderContents only cleans .drawer__inner)
          cartDrawer.classList.remove('is-empty');
          cartDrawer.querySelector('.drawer__inner')?.classList.remove('is-empty');

          // parsedState from /cart/add.js with sections has the right shape for renderContents
          if (typeof cartDrawer.renderContents === 'function' && parsedState?.sections) {
            cartDrawer.renderContents(parsedState);
          } else {
            // Fallback: open directly and fire a pub/sub refresh so the drawer content updates
            if (typeof cartDrawer.open === 'function') cartDrawer.open();
            else cartDrawer.classList.add('animate', 'active');
            if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'combo-builder', cartData: parsedState });
            }
          }
          return;
        }

        if (cartNotification) {
          if (typeof cartNotification.renderContents === 'function' && parsedState?.sections) {
            cartNotification.renderContents(parsedState);
          } else {
            if (typeof cartNotification.open === 'function') cartNotification.open();
            if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'combo-builder', cartData: parsedState });
            }
          }
          return;
        }

        // Fallback
        window.location.href = '/cart';
      } catch (e) {
        showError(e?.message || 'Erro ao adicionar');
      } finally {
        if (addBtn) {
          addBtn.disabled = false;
          if (addBtn.lastChild?.nodeType === Node.TEXT_NODE && addBtnOriginalText) {
            addBtn.lastChild.textContent = ' ' + addBtnOriginalText;
          }
        }
      }
    });

    // Search
    sectionRoot.querySelectorAll('[data-combo-search]').forEach((input) => {
      input.addEventListener('input', () => {
        applyLocalSearch();
      });
    });

    // Search button just focuses the input (since search is client-side)
    sectionRoot.querySelectorAll('[data-combo-search-btn]').forEach((btn) => {
      btn.addEventListener('click', () => {
        sectionRoot.querySelector('[data-combo-search]')?.focus();
      });
    });

    // Sort select
    sectionRoot.querySelectorAll('[data-combo-sort-select]').forEach((select) => {
      select.addEventListener('change', async () => {
        const u = new URL(window.location.href);
        u.searchParams.set('sort_by', select.value);
        try {
          await applyFiltersFromSearch(u.search);
        } catch (e) {
          showError(e?.message || 'Erro ao ordenar');
        }
      });
    });

    // Init
    resolveMaxItems();
    wireGridEvents();
    renderSlots();

    // Always fetch facets + grid via AJAX on first load — same path as clicking a collection button.
    // Relying on the server-side Liquid render for facets is unreliable: the `<form>` is always
    // present (hasFacetUI was always true) but the actual filter options only come from the
    // ?view=combo-builder endpoint, so we always refresh from there.
    (async () => {
      try {
        await setActiveCollection(state.activeCollectionHandle);
      } catch (e) {
        // ignore — grid was already rendered by Liquid as fallback
      }
    })();
  }

  function boot() {
    const sections = document.querySelectorAll('section[class*="combo-builder-"]');
    sections.forEach((section) => {
      initComboBuilder(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
